import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LucideIcon } from 'lucide-react';
import { MiniSparkline } from './MiniSparkline';

interface MetricCardProps {
  label: string;
  value: string | number | ReactNode;
  color?: string;
  tooltip?: string;
  description?: string;
  onClick?: () => void;
  actions?: ReactNode;
  className?: string;
  inlineActions?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
  sparklineData?: number[];
  sparklineColor?: string;
}

export function MetricCard({ 
  label, 
  value, 
  color = 'text-[#1F2937]', 
  tooltip, 
  description,
  onClick, 
  actions,
  className,
  inlineActions = false,
  isLoading = false,
  icon: Icon,
  sparklineData,
  sparklineColor = '#4B7BFF'
}: MetricCardProps) {
  const isClickable = !!onClick;

  // Loading state
  if (isLoading) {
    return (
      <Card className={cn(
        "h-[100px] rounded-[14px] border border-[#E4EBFF]",
        "shadow-[0_0_10px_rgba(0,0,0,0.03)]",
        "bg-gradient-to-b from-white to-[#F9FBFF]",
        "flex flex-col justify-center px-5 py-4",
        className
      )}>
        <div className="flex flex-col gap-2">
          <div className="h-4 bg-[#F3F6FF] animate-pulse rounded w-20" />
          <div className="h-7 bg-[#F3F6FF] animate-pulse rounded w-16" />
        </div>
      </Card>
    );
  }

  const cardContent = (
    <Card 
      className={cn(
        // Base styles PRD Mobile: altura 80px mobile, 100px desktop
        "min-h-[80px] md:h-[100px] rounded-[16px] border border-[#E4EBFF]",
        // Sombra premium mobile: 0 4px 10px rgba(0,0,0,0.06)
        "shadow-[0_4px_10px_rgba(0,0,0,0.06)] md:shadow-[0_2px_10px_rgba(0,0,0,0.03)]",
        "bg-white md:bg-gradient-to-b md:from-white md:to-[#F9FBFF]",
        "flex flex-col justify-center",
        // Padding mobile: 16px, desktop: 22px
        "p-4 md:p-[22px] relative overflow-hidden",
        // Hover effects premium (only desktop)
        "md:hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]",
        "md:hover:translate-y-[-2px]",
        "md:hover:border-[#E4EBFF]",
        "transition-all duration-200 ease-out",
        // Cursor
        isClickable && "cursor-pointer hover:border-[#4773F7]/30",
        className
      )}
      onClick={onClick}
      title={tooltip}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* PRD Mobile: ícones 18px mobile, 24px desktop */}
            {Icon && (
              <Icon 
                className="w-[18px] h-[18px] md:w-[24px] md:h-[24px] text-[#2F67FF]" 
                strokeWidth={1.8}
                aria-label={label}
              />
            )}
            {/* PRD Mobile: labels 12px uppercase, cor #7D8DA6 */}
            <span className="text-[12px] md:text-[11px] font-medium text-[#7D8DA6] uppercase tracking-wide whitespace-nowrap">
              {label}
            </span>
          </div>
          {actions && !inlineActions && <div className="flex items-center gap-1">{actions}</div>}
        </div>
        {inlineActions ? (
          <div className="flex items-center gap-2">
            {/* Valores: 16px com peso 500 */}
            <div className={cn("text-[16px] font-medium leading-none text-[#1B1F3B]", color)}>
              {value}
            </div>
            {actions && <div className="flex items-center gap-1">{actions}</div>}
          </div>
        ) : (
          <div className={cn("text-[16px] font-medium leading-none text-[#1B1F3B]", color)}>
            {value}
          </div>
        )}
      </div>
      
      {/* Sparkline - hidden on mobile */}
      {sparklineData && sparklineData.length > 0 && (
        <div className="hidden sm:block absolute bottom-3 right-3">
          <MiniSparkline 
            data={sparklineData} 
            color={sparklineColor}
            width={100}
            height={32}
          />
        </div>
      )}
    </Card>
  );

  if (description) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            {cardContent}
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="!max-w-[220px] p-3 text-sm leading-relaxed rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-[#E4EBFF] bg-white"
          >
            {description}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardContent;
}
